import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Users, UserPlus, BarChart3, Loader2, ArrowLeft, AlertTriangle, Calendar, Award } from 'lucide-react';
import { metricsAPI, handleMetricsAPIError, metricsFormatters } from './api';
import DefaultersView from './DefaultersView';
import GroupRatingsView from './GroupRatingsView';

const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'blue-600', trend, isDark, onClick, clickable = false }) => {
  const colorMap = {
    'blue-600': { border: 'border-blue-600', text: 'text-blue-600', bg: isDark ? 'bg-blue-900/30' : 'bg-blue-50' },
    'green-600': { border: 'border-green-600', text: 'text-green-600', bg: isDark ? 'bg-green-900/30' : 'bg-green-50' },
    'orange-600': { border: 'border-orange-600', text: 'text-orange-600', bg: isDark ? 'bg-orange-900/30' : 'bg-orange-50' },
    'purple-600': { border: 'border-purple-600', text: 'text-purple-600', bg: isDark ? 'bg-purple-900/30' : 'bg-purple-50' }
  };

  const colors = colorMap[color] || colorMap['blue-600'];

  return (
    <div 
      className={`rounded-lg shadow-sm border h-full ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'} ${clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={clickable ? onClick : undefined}
    >
      <div className={`h-1 rounded-t-lg ${colors.border.replace('border-', 'bg-')}`}></div>
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className={`text-3xl font-bold ${colors.text} mb-1`}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </div>
            <div className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {title}
            </div>
            {subtitle && (
              <div className={`text-xs ${trend?.includes('+') ? 'text-green-600' : isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                {subtitle}
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colors.bg}`}>
            <Icon className={`w-6 h-6 ${colors.text}`} />
          </div>
        </div>
      </div>
    </div>
  );
};

const ActiveAnalysisView = ({ isDark, onBack }) => {
  const [activeAnalysisGroups, setActiveAnalysisGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchActiveAnalysisGroups();
  }, []);

  const fetchActiveAnalysisGroups = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await metricsAPI.getActiveAnalysisGroups();
      if (response.success) {
        setActiveAnalysisGroups(response.data || []);
      } else {
        setError('Failed to load active analysis groups');
      }
    } catch (err) {
      setError(handleMetricsAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'closed': {
        bg: isDark ? 'bg-green-900/30' : 'bg-green-100',
        text: 'text-green-600',
        label: 'Closed'
      },
      'active': {
        bg: isDark ? 'bg-blue-900/30' : 'bg-blue-100',
        text: 'text-blue-600',
        label: 'Active'
      },
      'open': {
        bg: isDark ? 'bg-orange-900/30' : 'bg-orange-100',
        text: 'text-orange-600',
        label: 'Open'
      }
    };

    const config = statusConfig[status] || statusConfig['open'];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const countByStatus = (status) => {
    return activeAnalysisGroups.filter(g => g.status === status).length;
  };

  if (loading) {
    return (
      <div>
        <button
          onClick={onBack}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-4 ${
            isDark 
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Performance Analytics
        </button>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Loading active analysis groups...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <button
          onClick={onBack}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-4 ${
            isDark 
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Performance Analytics
        </button>
        <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
          <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
          <button 
            onClick={fetchActiveAnalysisGroups}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Back Button */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors mb-4 ${
            isDark 
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Performance Analytics
        </button>
        
        <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Groups with open or active performance periods that are pending finalization
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Pending</div>
          <div className="text-2xl font-bold text-orange-600">
            {activeAnalysisGroups.length}
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Open Status</div>
          <div className="text-2xl font-bold text-orange-600">
            {countByStatus('open')}
          </div>
        </div>
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Active Status</div>
          <div className="text-2xl font-bold text-blue-600">
            {countByStatus('active')}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  #
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Group Name
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Last Meeting Date
                </th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Officer
                </th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {activeAnalysisGroups.map((group, index) => (
                <tr key={index} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {group.group_name}
                    </div>
                    {group.period_name && (
                      <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {group.period_name}
                      </div>
                    )}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {group.last_meeting_date}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className={`px-2 py-1 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      {group.officer_initials}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {getStatusBadge(group.status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {activeAnalysisGroups.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            No active analysis periods found
          </div>
        )}
      </div>
    </div>
  );
};

const PerformanceTab = ({ isDark, selectedPeriod = 'month' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [showActiveAnalysisView, setShowActiveAnalysisView] = useState(false);
  const [showDefaultersView, setShowDefaultersView] = useState(false);
  const [showTrackableDefaultersView, setShowTrackableDefaultersView] = useState(false);
  const [defaultersData, setDefaultersData] = useState(null);
  const [showGroupRatings, setShowGroupRatings] = useState(false);
  const [topRatedGroups, setTopRatedGroups] = useState([]);

  useEffect(() => {
    fetchPerformanceData();
    fetchDefaultersData();
    fetchTopRatings();
  }, [selectedPeriod]);

  const fetchDefaultersData = async () => {
    try {
      const response = await metricsAPI.getDefaultersSummary();
      if (response.success) {
        setDefaultersData(response.data);
      }
    } catch (err) {
      console.error('Error fetching defaulters:', err);
    }
  };

  const fetchTopRatings = async () => {
    try {
      // Get current month in YYYY-MM-DD format
      const now = new Date();
      const targetMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
      
      const response = await metricsAPI.getGroupRatings({
        target_month: targetMonth
      });
      
      if (response.success) {
        const data = Array.isArray(response.data) ? response.data : (response.data?.groups || []);
        // Sort by total score and take top 5
        const topGroups = data
          .sort((a, b) => b.current_month.scores.total - a.current_month.scores.total)
          .slice(0, 5);
        setTopRatedGroups(topGroups);
      }
    } catch (err) {
      console.error('Error fetching top ratings:', err);
    }
  };

  const fetchPerformanceData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await metricsAPI.getPerformanceMetrics({ 
        period: selectedPeriod 
      });
      
      if (response.success) {
        setPerformanceData(response.data);
      } else {
        setError('Failed to load performance data');
      }
    } catch (err) {
      setError(handleMetricsAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  if (showGroupRatings) {
  return <GroupRatingsView isDark={isDark} onBack={() => setShowGroupRatings(false)} />;
}

  // Show Defaulters View if toggled
  if (showDefaultersView) {
    return <DefaultersView 
      isDark={isDark} 
      onBack={() => setShowDefaultersView(false)} 
      trackMonthly={false}
    />;
  }
  
  if (showTrackableDefaultersView) {
    return <DefaultersView 
      isDark={isDark} 
      onBack={() => setShowTrackableDefaultersView(false)} 
      trackMonthly={true}
    />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Loading performance data...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
        <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
        <button 
          onClick={fetchPerformanceData}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!performanceData) {
    return (
      <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
        No performance data available
      </div>
    );
  }

  // Show Active Analysis View if toggled
  if (showActiveAnalysisView) {
    return <ActiveAnalysisView isDark={isDark} onBack={() => setShowActiveAnalysisView(false)} />;
  }

  return (
    <div>
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <MetricCard
          title="Total Clients"
          value={performanceData.total_clients?.value || 0}
          subtitle={performanceData.total_clients?.change || 'N/A'}
          icon={Users}
          color="blue-600"
          isDark={isDark}
        />
        <MetricCard
          title="Total Groups"
          value={performanceData.total_groups?.value || 0}
          subtitle={performanceData.total_groups?.change || 'N/A'}
          icon={Users}
          color="blue-600"
          isDark={isDark}
        />
        <MetricCard
          title="New Monthly Members"
          value={performanceData.new_members?.value || 0}
          subtitle={performanceData.new_members?.change || 'N/A'}
          icon={UserPlus}
          color="green-600"
          isDark={isDark}
        />
        <MetricCard
          title="Active Analysis (Pending)"
          value={performanceData.active_periods?.value || 0}
          subtitle={performanceData.active_periods?.change || 'N/A'}
          icon={BarChart3}
          color="orange-600"
          isDark={isDark}
          clickable={true}
          onClick={() => setShowActiveAnalysisView(true)}
        />
        <MetricCard
          title="Active Defaulters"
          value={defaultersData?.active_count || 0}
          subtitle={defaultersData?.active_total_default ? 
            `Total Arrears: ${metricsFormatters.formatCurrency(defaultersData.active_total_default)}` : 
            'No arrears'
          }
          icon={AlertTriangle}
          color="orange-600"
          isDark={isDark}
          clickable={true}
          onClick={() => setShowDefaultersView(true)}
        />
        <MetricCard
          title="Trackable Defaulters"
          value={defaultersData?.active_defaulters?.filter(d => d.can_track_monthly).length || 0}
          subtitle="With monthly payment tracking"
          icon={Calendar}
          color="purple-600"
          isDark={isDark}
          clickable={true}
          onClick={() => setShowTrackableDefaultersView(true)}
        />
      </div>

      {/* Performance Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className={`rounded-lg shadow-sm border p-6 ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center mb-4">
            <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Top Performing Clients
            </h3>
          </div>
          <div className="space-y-4">
            {performanceData.top_clients && performanceData.top_clients.length > 0 ? (
              performanceData.top_clients.map((client, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold ${
                      index < 3 ? 'bg-green-500' : 'bg-blue-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="ml-3">
                      <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {client.name}
                      </div>
                      <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {client.loans} active loans
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {client.percentage}%
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      client.status === 'excellent' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {client.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>No data available</p>
            )}
          </div>
        </div>

        <div className={`rounded-lg shadow-sm border p-6 ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Users className="w-5 h-5 text-green-600 mr-2" />
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Top Performing Groups
              </h3>
            </div>
            <button
              onClick={() => setShowGroupRatings(true)}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Award className="w-4 h-4" />
              View All Ratings
            </button>
          </div>
          <div className="space-y-4">
            {topRatedGroups && topRatedGroups.length > 0 ? (
              topRatedGroups.map((group, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold ${
                      index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-600' : 'bg-green-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="ml-3">
                      <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {group.group_name}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {group.current_month.period_name}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        group.current_month.scores.grade?.startsWith('A') 
                          ? 'bg-green-100 text-green-800' 
                          : group.current_month.scores.grade?.startsWith('B')
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {group.current_month.scores.grade || 'N/A'}
                      </span>
                      <div className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {group.current_month.scores.total.toFixed(1)}
                      </div>
                    </div>
                    <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {group.changes?.month_to_month > 0 && (
                        <span className="text-green-600">
                          ↑ {group.changes.month_to_month.toFixed(1)} pts
                        </span>
                      )}
                      {group.changes?.month_to_month < 0 && (
                        <span className="text-red-600">
                          ↓ {Math.abs(group.changes.month_to_month).toFixed(1)} pts
                        </span>
                      )}
                      {(!group.changes?.month_to_month || group.changes?.month_to_month === 0) && (
                        <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
                          No change
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                {topRatedGroups.length === 0 ? 'Loading ratings...' : 'No data available'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Groups Requiring Attention */}
      <div className={`rounded-lg shadow-sm border p-6 ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center mb-4 text-red-600">
          <TrendingDown className="w-5 h-5 mr-2" />
          <h3 className={`text-lg font-semibold ${isDark ? 'text-red-400' : 'text-red-600'}`}>
            Groups Requiring Attention
          </h3>
        </div>
        <div className="space-y-4">
          {performanceData.attention_groups && performanceData.attention_groups.length > 0 ? (
            performanceData.attention_groups.map((group, index) => (
              <div key={index} className="flex items-center justify-between py-2">
                <div className="flex-1">
                  <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {group.name}
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {group.defaults} defaulters out of {group.total} members
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Collection: {group.collection}%
                    </div>
                    <div className={`w-24 rounded-full h-2 mt-1 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div 
                        className={`h-2 rounded-full ${group.risk === 'high' ? 'bg-red-500' : 'bg-orange-500'}`}
                        style={{ width: `${group.collection}%` }}
                      ></div>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    group.risk === 'high' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
                  }`}>
                    {group.risk} risk
                  </span>
                </div>
              </div>
            ))
          ) : (
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>No groups requiring attention</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceTab;